/**
 * presets — the Design PRESET library: a reusable, platform-FREE set of named
 * designs that replaces the old per-platform `designTemplates`. Each preset owns
 * its own canvas; a design_render node references one by id, and any preset works
 * with any destination.
 *
 *   Preset = { id, name, doc }   // doc = a generalized design doc (validateDesignDoc)
 *
 * The library lives in the `socialAutomation` Setting as `designPresets`. This
 * module owns the shape, the on-brand seeds, lookups, and the one-time migration
 * from the legacy `designTemplates` map — used by BOTH ends (the duck/editor and
 * the server's getConfig + design_render run body), so there is ONE source of truth.
 *
 * ESM module, ZERO node-only deps (Node 24 require()-of-ESM loads it server-side;
 * the CRA client imports it directly) — mirrors designDoc.js / imageSpec.js.
 *
 * @module DesignEditor/shared/presets
 */
import { CANVAS_PRESETS } from './platformSpecs.js';
import { buildDefaultPreset, buildStoryHighlightsDoc } from './defaultTemplates.js';

// The seed canvas sizes a fresh library ships with, and the preset every seeded
// graph references by default. Keep DEFAULT_PRESET_ID inside SEED_SIZES.
const SEED_SIZES = ['portrait', 'square', 'landscape'];
const DEFAULT_PRESET_ID = 'preset-portrait';

// The 1200×630 landscape seed — the default FEED design (Facebook/Community feed
// posts). A named export so the canonical default graph references it by id
// instead of a bare string.
const LANDSCAPE_PRESET_ID = 'preset-landscape';

// The bespoke Instagram-Story design (9:16, baked brand backdrop + dynamic
// location/photo/CTA). Shipped as a built-in seed so it appears in the design
// node's preset dropdown out of the box; an Instagram exit set to "Story" points
// its design_render node here. Stable id so seeds/graphs can reference it.
const STORY_HIGHLIGHTS_PRESET_ID = 'preset-story-highlights';

const PLATFORM_LABELS = { instagram: 'Instagram', facebook: 'Facebook', linkedin: 'LinkedIn' };

/** @returns {boolean} whether a value is a usably-shaped preset. */
const isPreset = p =>
    Boolean(p) &&
    typeof p === 'object' &&
    typeof p.id === 'string' &&
    p.id !== '' &&
    Boolean(p.doc);

/**
 * The on-brand starter presets a fresh library ships with — one per seed canvas
 * size, named "<label> <aspect>" (e.g. "Hochformat 4:5"). DEFAULT_PRESET_ID is
 * always among them, so a seeded graph's presetId always resolves.
 *
 * @returns {Array<{id:string,name:string,doc:object}>}
 */
const buildSeedPresets = () => [
    ...SEED_SIZES.map(sizeId => {
        const c = CANVAS_PRESETS.find(x => x.id === sizeId);
        return {
            id: `preset-${sizeId}`,
            name: `${c.label} ${c.aspect}`,
            doc: buildDefaultPreset(sizeId),
        };
    }),
    {
        id: STORY_HIGHLIGHTS_PRESET_ID,
        name: 'Highlights Story 9:16',
        doc: buildStoryHighlightsDoc(),
    },
];

/** Find a preset by id (or null). */
const findPreset = (presets, id) =>
    Array.isArray(presets) ? presets.find(p => p.id === id) || null : null;

/** The design doc of the preset with `id`, or null if there is no such preset. */
const findPresetDoc = (presets, id) => {
    const p = findPreset(presets, id);
    return p ? p.doc : null;
};

// Dedup a preset list by id, keeping the FIRST occurrence (seeds win over a
// same-id migrated entry — there is no id collision today, but this keeps the
// merge total/deterministic).
const dedupById = list => {
    const seen = new Set();
    const out = [];
    for (const p of list) {
        if (!isPreset(p) || seen.has(p.id)) continue;
        seen.add(p.id);
        out.push(p);
    }
    return out;
};

/**
 * Resolve the preset library for a settings value — the ONE migration seam.
 *  - if `designPresets` is already present, use it (filtered to valid shapes);
 *  - else seed the on-brand starters AND fold any legacy per-platform
 *    `designTemplates` in as named presets (so a manager's customised templates
 *    survive the cutover);
 *  - a totally empty settings yields just the seeds.
 *
 * @param {object} [settings] the socialAutomation Setting value
 * @returns {Array<{id:string,name:string,doc:object}>}
 */
const normalizePresets = (settings = {}) => {
    const existing = Array.isArray(settings.designPresets)
        ? settings.designPresets.filter(isPreset)
        : [];
    if (existing.length) return existing;

    const templates = (settings && settings.designTemplates) || {};
    const migrated = Object.keys(templates)
        .map(platform => ({
            id: `preset-${platform}`,
            name: PLATFORM_LABELS[platform] || platform,
            doc: templates[platform],
        }))
        .filter(isPreset);

    return dedupById([...buildSeedPresets(), ...migrated]);
};

// A fresh, collision-resistant preset id. App-side only (Date.now/Math.random are
// fine here — the no-Date/random rule is a Workflow-script constraint, not app code).
const makePresetId = () =>
    `preset-${Date.now().toString(36)}-${Math.random()
        .toString(36)
        .slice(2, 8)}`;

/** Wrap a name + doc into a new library preset with a fresh id. */
const makePreset = (name, doc) => ({ id: makePresetId(), name: name || 'Neues Preset', doc });

export {
    DEFAULT_PRESET_ID,
    LANDSCAPE_PRESET_ID,
    STORY_HIGHLIGHTS_PRESET_ID,
    isPreset,
    buildSeedPresets,
    findPreset,
    findPresetDoc,
    normalizePresets,
    makePresetId,
    makePreset,
};
